import { Button, CircularProgress, Grid, Link, TableCell, TableRow, TextField } from '@mui/material';
import { createLaborClassification, getLaborClassifications, updateLaborClassification } from 'api';
import { Page, Table } from 'components';
import {
  EquipmentDto,
  equipmentInit,
  LaborClassificationDto,
  laborClassificationInit,
  LaborClassificationsParameters,
  laborClassificationsParametersInit
} from 'dtos';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import AddEditLaborClassDialog from './AddEditLaborClassDialog';

const LaborClassifications = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [filterText, setFilterText] = useState<string>('');
  const [isCreatingLaborClassification, setIsCreatingLaborClassification] = useState<boolean>(false);
  const [isGettingLaborClassifications, setIsGettingLaborClassifications] = useState<boolean>(false);
  const [isUpdatingLaborClassification, setIsUpdatingLaborClassification] = useState<boolean>(false);
  const [laborClassifications, setLaborClassifications] = useState<LaborClassificationDto[]>([]);
  const [laborClassificationsDialogOpen, setLaborClassificationsDialogOpen] = useState<boolean>(false);
  const [parameters, setParameters] = useState<LaborClassificationsParameters>(laborClassificationsParametersInit);
  const [selectedLaborClassification, setSelectedLaborClassification] = useState<EquipmentDto>(equipmentInit);
  const [totalLaborClassifications, setTotalLaborClassifications] = useState<number>(0);

  useEffect(() => {
    onFilter();
  }, [parameters]);

  const onFilter = () => {
    setIsGettingLaborClassifications(true);
    getLaborClassifications(parameters)
      .then(({ totalCount, value }) => {
        setTotalLaborClassifications(totalCount!);
        setLaborClassifications(value);
      })
      .finally(() => setIsGettingLaborClassifications(false));
  };

  const onSortChange = (propertyName: string, sortDirection: 'asc' | 'desc') => {
    setParameters({
      ...parameters,
      sortBy: propertyName,
      sortDirection
    });
  };

  const onChangePage = (page: number) => {
    setParameters({
      ...parameters,
      page
    });
  };

  const onChangePageSize = (pageSize: number) => {
    setParameters({
      ...parameters,
      pageSize
    });
  };

  const onReset = () => {
    setParameters({ ...parameters, filterText: '' });
    setFilterText('');
  };

  const onAddLaborClassification = () => {
    setSelectedLaborClassification(laborClassificationInit);
    setLaborClassificationsDialogOpen(true);
  };

  const onEditLaborClassification = (laborClassification: LaborClassificationDto) => {
    setSelectedLaborClassification(laborClassification);
    setLaborClassificationsDialogOpen(true);
  };

  const onCloseLaborClassificationDialog = () => {
    setLaborClassificationsDialogOpen(false);
  };

  const onSaveLaborClassificationDialog = (values: LaborClassificationDto) => {
    if (values.id) {
      setIsUpdatingLaborClassification(true);
      updateLaborClassification(values)
        .then(() => {
          onFilter();
          enqueueSnackbar(`${values.name} updated successfully!`, { variant: 'success' });
          onCloseLaborClassificationDialog();
        })
        .finally(() => {
          setIsUpdatingLaborClassification(false);
        });
    } else {
      setIsCreatingLaborClassification(true);
      createLaborClassification(values)
        .then(() => {
          onFilter();
          enqueueSnackbar(`${values.name} created successfully!`, { variant: 'success' });
          onCloseLaborClassificationDialog();
        })
        .finally(() => {
          setIsCreatingLaborClassification(false);
        });
    }
  };

  return (
    <>
      <AddEditLaborClassDialog
        isLoading={isCreatingLaborClassification || isUpdatingLaborClassification}
        laborClassification={selectedLaborClassification}
        onClose={onCloseLaborClassificationDialog}
        onSave={onSaveLaborClassificationDialog}
        open={laborClassificationsDialogOpen}
      />

      <Page title='Labor Classifications' isLoading={isGettingLaborClassifications}>
        <Table
          actions={
            <Grid container justifyContent='space-between'>
              <Grid item xs={12} sm={6} container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label='Filter text'
                    onChange={e => setFilterText(e.target.value)}
                    size='small'
                    value={filterText}
                  />
                </Grid>

                <Grid item>
                  <Button color='inherit' onClick={() => setParameters({ ...parameters, filterText })} variant='contained'>
                    FILTER
                  </Button>
                </Grid>

                <Grid item>
                  <Button color='inherit' onClick={onReset} variant='contained'>
                    RESET
                  </Button>
                </Grid>
              </Grid>

              <Grid item xs={12} sm={6} container justifyContent='flex-end'>
                <Grid item>
                  <Button
                    variant='contained'
                    color='inherit'
                    onClick={onAddLaborClassification}
                    disabled={isCreatingLaborClassification || isUpdatingLaborClassification}
                  >
                    {isCreatingLaborClassification && (
                      <>
                        <CircularProgress color='inherit' size={20} />
                        &nbsp;&nbsp;
                      </>
                    )}
                    ADD NEW
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          }
          columns={[{ label: 'Name', propertyName: 'name' }]}
          sortDirection={parameters.sortDirection}
          sortBy={parameters.sortBy}
          onSortChange={onSortChange}
          onChangePage={onChangePage}
          onChangePageSize={onChangePageSize}
          page={parameters.page!}
          pageSize={parameters.pageSize!}
          totalCount={totalLaborClassifications}
        >
          {laborClassifications.map(laborClassification => (
            <TableRow key={laborClassification.id}>
              <TableCell>
                <Link
                  onClick={() => onEditLaborClassification(laborClassification)}
                  sx={{
                    cursor: 'pointer',
                    textDecoration: 'none'
                  }}
                >
                  {laborClassification.name}
                </Link>
              </TableCell>
            </TableRow>
          ))}
        </Table>
      </Page>
    </>
  );
};

export default LaborClassifications;
